import React, { useEffect, useRef, useState } from 'react';
import { Theme } from '@emotion/react';
import { Button, Typography } from '@mui/material';
import { Box, Stack, SxProps } from '@mui/system';

import { DistanceField } from '@paragon/material';

import {
  doesTransformableTypeSupportVerticalMovement,
  TransformableType,
} from '../../../../library/transformations/types';
import {
  applyExplicitTransformation,
  cancelTransformation,
} from '../../../../store/actions/layout/transformationActions';
import { useAppDispatch, useAppSelector } from '../../../hooks';

const ORIGIN = { x: 0, y: 0, z: 0 };

export function ManualMovementControls(props: { sx?: SxProps<Theme>; targetType: TransformableType }) {
  const dispatch = useAppDispatch();

  const transformation = useAppSelector((state) => state.application.transformingObjectsInfo);
  const verticalModeEnabled = doesTransformableTypeSupportVerticalMovement(props.targetType);

  const [xDistance, setXDistance] = useState(0);
  const [yDistance, setYDistance] = useState(0);
  const [zDistance, setZDistance] = useState(0);

  const xTranslationInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => void setTimeout(() => xTranslationInputRef.current?.focus()), []);

  function requestMovementAndClosePanel(x: number, y: number, z: number) {
    if (!transformation || transformation.type !== 'movement') return;

    dispatch(
      applyExplicitTransformation({
        ...transformation,

        origin: ORIGIN,
        destination: { x, y, z },
      }),
    );
  }

  return (
    <Box sx={props.sx}>
      <Typography sx={{ marginBottom: 2 }}>or, manually input a specific distance to move:</Typography>

      <Stack direction="row" spacing={1} sx={{ marginBottom: 1 }}>
        <DistanceField
          inputRef={xTranslationInputRef}
          label="X Translation"
          value={xDistance}
          onSubmit={(value, { finalizing }) => {
            if (finalizing) {
              requestMovementAndClosePanel(value, yDistance, zDistance);
            } else {
              setXDistance(value);
            }
          }}
        />
        <DistanceField
          label="Y Translation"
          value={yDistance}
          onSubmit={(value, { finalizing }) => {
            if (finalizing) {
              requestMovementAndClosePanel(xDistance, value, zDistance);
            } else {
              setYDistance(value);
            }
          }}
        />
        {verticalModeEnabled && (
          <DistanceField
            label="Z Translation"
            value={zDistance}
            onSubmit={(value, { finalizing }) => {
              if (finalizing) {
                requestMovementAndClosePanel(xDistance, yDistance, value);
              } else {
                setZDistance(value);
              }
            }}
          />
        )}
      </Stack>

      <Box sx={{ padding: 1, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={() => dispatch(cancelTransformation())}>
          Cancel
        </Button>

        <Button variant="contained" onClick={() => requestMovementAndClosePanel(xDistance, yDistance, zDistance)}>
          Apply
        </Button>
      </Box>
    </Box>
  );
}
